<script setup>
import { ref, computed, defineProps } from 'vue';
import MyLoadsDesktopButtonComponent from '@/components/myLoads/MyLoadsDesktopButtonComponent.vue';
import RadioButtonDesktopComponent from '@/components/RadioButtonDesktopComponent.vue';
import RadioButtonMobileComponent from '@/components/RadioButtonMobileComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';

defineProps({
  selectedVehicle: {
    type: Object,
    required: true,
    default: () => {},
  },
  isEquipmentTable: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const { screenSize } = useScreenSize();

const isDesktop = computed(() => screenSize.value === 'desktop');

defineEmits(['closeModal', 'assignVehicleClick', 'setEldExemptStatusClick']);

const stepsForResolvingNoEldConnection = [
  'Ensure the correct ELD account is connected in Highway.',
  'Make sure the VIN correctly listed in the ELD account.',
  'Check that the ELD device in the truck working and active.',
  'Ensure that the correct permissions are set up in this ELD account.',
];

const showEldExemptions = ref(false);

const exemptReasons = [
  {
    id: 'vehicle_manufactured_before_2000',
    label: 'Vehicle Manufactured before 2000',
  },
  { id: 'vehicle_has_glider_kit', label: 'Vehicle has a Glider Kit' },
  {
    id: 'driver_does_not_travel_over_150_miles',
    label: 'Driver does not travel over 150 miles',
  },
];

const exemptReasonSelected = ref(null);

const handleSelectRadio = (reason) => {
  exemptReasonSelected.value = reason;
};
</script>

<template>
  <div
    class="flex w-full max-w-fit flex-col gap-6 rounded-xl bg-white p-6 shadow-[0px_0px_1px_0px_rgba(31,36,46,0.31),0px_8px_12px_0px_rgba(31,36,46,0.16)]"
  >
    <div class="flex flex-col gap-4">
      <div class="flex w-full items-center justify-between gap-4">
        <div class="flex gap-2">
          <div
            class="flex-1 justify-start text-xl font-medium leading-7 text-steel-1100"
          >
            {{
              showEldExemptions
                ? 'Select the Exemption Reason'
                : 'No Active ELD Connection'
            }}
          </div>
        </div>
        <MyLoadsDesktopButtonComponent
          button-class="subtle-button rounded-[5px]"
          button-type="button"
          @button-click="$emit('closeModal')"
        >
          <template #leftIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M18.3639 7.05044L16.9497 5.63623L11.9999 10.586L7.0502 5.63623L5.63599 7.05044L10.5857 12.0002L5.63599 16.9499L7.0502 18.3642L11.9999 13.4144L16.9497 18.3642L18.3639 16.9499L13.4141 12.0002L18.3639 7.05044Z"
                fill="#6C7C9D"
              />
            </svg>
          </template>
        </MyLoadsDesktopButtonComponent>
      </div>
      <div
        v-if="!showEldExemptions"
        class="w-[85%] text-sm leading-tight tracking-tight"
      >
        Please check the following steps below or mark the vehicle as ELD
        exempt.
      </div>
    </div>

    <div class="flex flex-col gap-6">
      <div class="flex w-fit gap-10 rounded-[5px] bg-steel-100 p-3">
        <div class="flex flex-col gap-1">
          <div class="text-xs font-semibold leading-none text-steel-800">
            Truck Number
          </div>
          <div
            class="text-sm font-normal capitalize leading-tight text-steel-900"
          >
            {{ selectedVehicle?.truck_number || '-' }}
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <div class="text-xs font-semibold leading-none text-steel-800">
            VIN
          </div>
          <div
            class="text-sm font-normal capitalize leading-tight text-steel-900"
          >
            {{ selectedVehicle?.vin || '-' }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col items-center justify-start gap-6">
      <div
        v-if="!showEldExemptions"
        class="flex w-full flex-col items-start justify-center gap-6"
      >
        <div
          v-for="step in stepsForResolvingNoEldConnection"
          :key="step"
          class="flex w-full flex-col items-center justify-start gap-4"
        >
          <div class="flex w-full gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
            >
              <path
                d="M7.00024 0.333344C8.97816 0.333438 10.754 1.19566 11.9749 2.56381V2.56479C11.8988 2.5895 11.8323 2.6556 11.7004 2.78745L7.04419 7.44467C6.91218 7.57668 6.84588 7.6426 6.76978 7.66733C6.70286 7.68906 6.63064 7.68906 6.56372 7.66733C6.48761 7.6426 6.42131 7.57667 6.28931 7.44467L4.63306 5.78745C4.50121 5.6556 4.43471 5.5895 4.35864 5.56479C4.29181 5.54317 4.2194 5.54308 4.15259 5.56479C4.07665 5.58956 4.01077 5.65582 3.87915 5.78745L3.45435 6.21225C3.32247 6.34413 3.25641 6.41059 3.23169 6.48666C3.21008 6.55349 3.20998 6.62591 3.23169 6.69272C3.2565 6.76863 3.32271 6.83452 3.45435 6.96616L6.28931 9.80112C6.42125 9.93306 6.48766 9.99902 6.56372 10.0238C6.63067 10.0455 6.70283 10.0455 6.76978 10.0238C6.84583 9.99901 6.91226 9.93304 7.04419 9.80112L12.8792 3.96616C12.8923 3.95298 12.9034 3.93903 12.9153 3.92709C13.3941 4.84688 13.6663 5.89168 13.6663 7.00034C13.6661 10.682 10.6819 13.6662 7.00024 13.6664C3.31845 13.6664 0.333428 10.6821 0.333252 7.00034C0.333252 3.31844 3.31835 0.333344 7.00024 0.333344Z"
                fill="#374053"
              />
            </svg>
            <div
              class="text-color-text-default justify-center font-['Poppins'] text-sm font-normal leading-tight tracking-tight"
            >
              {{ step }}
            </div>
          </div>
          <div
            class="h-0 w-full outline outline-1 outline-offset-[-0.50px] outline-gray-100"
          ></div>
        </div>
      </div>
      <div
        v-else
        class="max-h-[400px] w-full overflow-y-scroll"
      >
        <div v-if="isDesktop">
          <RadioButtonDesktopComponent
            v-for="exemptReason in exemptReasons"
            :key="exemptReason.id"
            :item="exemptReason.label || '-'"
            subtext=""
            name="exemptReason"
            :value="exemptReason.label"
            :show="false"
            :show-checkmark="false"
            :show-border-on-hover="false"
            :checked="exemptReason.id === exemptReasonSelected?.id"
            :handle-click="() => handleSelectRadio(exemptReason)"
          />
        </div>
        <!-- add mobile radio buttons -->
        <div v-else>
          <RadioButtonMobileComponent
            v-for="exemptReason in exemptReasons"
            :key="exemptReason.id"
            :item="exemptReason.label || '-'"
            subtext=""
            name="exemptReason"
            :value="exemptReason.label"
            :show="false"
            :show-checkmark="false"
            :show-border-on-hover="false"
            :checked="exemptReason.id === exemptReasonSelected?.id"
            :handle-click="() => handleSelectRadio(exemptReason)"
          />
        </div>
      </div>
      <div
        v-if="!isDesktop"
        class="flex items-center justify-between gap-2 self-stretch"
      >
        <MyLoadsDesktopButtonComponent
          button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
          :button-text="!showEldExemptions ? 'Mark as ELD Exempt' : 'Cancel'"
          text-class=" text-sm font-medium leading-10 tracking-tight"
          @button-click="
            !showEldExemptions
              ? (showEldExemptions = true)
              : $emit('closeModal')
          "
        />
        <MyLoadsDesktopButtonComponent
          button-class="flex-1 primary-button rounded-[3px] px-3 py-1.5"
          :button-text="
            !showEldExemptions
              ? !isEquipmentTable
                ? 'Change Vehicle'
                : 'Done'
              : 'Save'
          "
          text-class="text-sm font-medium leading-10 tracking-tight"
          @button-click="
            !showEldExemptions
              ? $emit('closeModal')
              : $emit('setEldExemptStatusClick', exemptReasonSelected)
          "
        />
      </div>
      <div
        v-else
        class="flex flex-col items-end justify-center gap-1 self-stretch"
      >
        <div class="flex items-start justify-start gap-4">
          <MyLoadsDesktopButtonComponent
            button-class="default-button rounded-[5px] px-3 py-1 h-8"
            text-class="font-medium text-sm leading-10 tracking-tight"
            :button-text="!showEldExemptions ? 'Mark as ELD Exempt' : 'Cancel'"
            @button-click="
              !showEldExemptions
                ? (showEldExemptions = true)
                : $emit('closeModal')
            "
          />
          <MyLoadsDesktopButtonComponent
            button-class="primary-button rounded-[5px] px-3 py-1 h-8"
            text-class="font-medium text-sm leading-10 tracking-tight whitespace-nowrap"
            :button-text="
              !showEldExemptions
                ? !isEquipmentTable
                  ? 'Change Vehicle'
                  : 'Done'
                : 'Save'
            "
            @button-click="
              !showEldExemptions
                ? $emit('closeModal')
                : $emit('setEldExemptStatusClick', exemptReasonSelected)
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
